import { BrowserRouter as Router, Routes, Route } from "react-router-dom"

import Home from "./pages/Home"

import "./styles/index.css"
import { lazy, Suspense, useEffect } from "react"
import { getAuthService } from "./services/authService"
import { useAppDispatch, useAppSelector } from "./redux/hooks"
import { authenticate } from "./redux/features/auth/authSlice"
import { History } from "./pages/History"
import { Wrapper } from "./layouts/Wrapper"
import { SignIn } from "./pages/auth/SignIn"
import { NoAuthRoute } from "./components/NoAuthRoute/NoAuthRoute"
import { ForgotPassword } from "./pages/auth/ForgotPassword"
import { CreateAccount } from "./pages/auth/CreateAccount"

import './App.css'
import { ConfirmAccount } from "./pages/auth/ConfirmAccount"
import { SecureRoute } from "./components/SecureRoute/SecureRoute"
import { Calendar } from "./pages/Calendar"
import { MembersRoute } from "./components/MembersRoute/MembersRoute"
import { MyAccount } from "./pages/MyAccount"
import { PrivateMessages } from "./pages/messages/PrivateMessages"
import { PrivateMessageThread } from "./pages/messages/PrivateMessageThread"
import { Rules } from "./pages/Rules"
import { Bylaws } from "./pages/Bylaws"
import { LoginCallback } from "./pages/auth/LoginCallback"
import { AccountsApi } from "./clients"
import { getConfiguration } from "./clients/configuration"
import { setCurrentUser } from "./redux/features/currentUser/currentUserSlice"
import { EmailUnsubscribe } from "./pages/EmailUnsubscribe"
import { AccountDetail } from "./pages/accounts/AccountDetail"
import { ResetPassword } from "./pages/auth/ResetPassword"
import { GenericSpinner } from "./components/GenericSpinner/GenericSpinner"
import { AdministratorsRoute } from "./components/AdministratorsRoute/AdministratorsRoute"

const App = () => {

    const dispatch = useAppDispatch()
    const authState = useAppSelector(state => state.auth.idTokenPayload)

    const AdminDashboardRoute = lazy(() => import('./pages/admin/AdminPanel'))
    const AccountsRoute = lazy(() => import('./pages/admin/Accounts'))
    const CampsiteDetailRoute = lazy(() => import('./pages/admin/CampsiteDetail'))
    const CampsitesRoute = lazy(() => import('./pages/admin/Campsites'))
    const ClubhouseRequestsRoute = lazy(() => import('./pages/admin/ClubhouseRequests'))
    const CreateCampsiteRoute = lazy(() => import('./pages/admin/CreateCampsite'))
    const CreateMemberRoute = lazy(() => import('./pages/admin/CreateMember'))
    const MembersManagementRoute = lazy(() => import('./pages/admin/MembersManagement'))
    const MembersManagementDetailRoute = lazy(() => import('./pages/admin/MembersManagementDetail'))
    const MessageBoardManagementRoute = lazy(() => import('./pages/admin/MessageBoardManagement'))

    const ListMembersRoute = lazy(() => import('./pages/members/Members'))
    const MemberDetailRoute = lazy(() => import('./pages/members/MembersDetail'))
    const MemberDashboardRoute = lazy(() => import('./pages/member-dashboard/MemberDashboard'))
    const MemberViewCampsitesRoute = lazy(() => import('./pages/member-dashboard/MemberViewCampsites'))
    const MessageBoardRoute = lazy(() => import('./pages/message-board/MessageBoard'))
    const NewThreadRoute = lazy(() => import('./pages/message-board/NewThread'))
    const MessageThreadRoute = lazy(() => import('./pages/message-board/MessageThread'))
    const EditMessageRoute = lazy(() => import('./pages/message-board/EditMessage'))

    useEffect(() => {
        const auth = getAuthService()
        auth.onTokensUpdated((tokens) => {
            dispatch(authenticate(tokens))
        })
        auth.authenticateSilently().then(res => {
            dispatch(authenticate(res))
        }).catch(() => {
            auth.logout()
        })
    }, [dispatch])

    useEffect(() => {
        const getCurrentUser = async (id: string) => {
            const config = await getConfiguration()
            const accountsApi = new AccountsApi(config)
            const account = await accountsApi.getAccount({ accountID: id })
            return account.data
        }
        if (authState) {
            getCurrentUser(authState.sub)
                .then(acc => {
                    dispatch(setCurrentUser({
                        ...acc,
                        createdDate: acc.createdDate.toISOString(),
                        updatedDate: acc.updatedDate.toISOString(),
                        lastSeenDate: acc.lastSeenDate.toISOString(),
                    }))
                })
        } else {
            dispatch(setCurrentUser(null))
        }
    }, [dispatch, authState])

    useEffect(() => {
        // Load reCAPTCHA script
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${import.meta.env.VITE_RECAPTCHA_KEY}`;
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
    }, [])
    
    return (
        <Router>
            <Suspense fallback={<GenericSpinner />}>
                <Wrapper>
                    <Routes>
                        <Route index element={<Home />} />
                        <Route path="/history" element={<History />} />
                        <Route path="/rules" element={<MembersRoute><Rules /></MembersRoute>} />
                        <Route path="/bylaws" element={<MembersRoute><Bylaws /></MembersRoute>} />
                        <Route path="/calendar" element={<MembersRoute><Calendar /></MembersRoute>} />
                        <Route path="/members">
                            <Route index element={<MembersRoute><ListMembersRoute /></MembersRoute>} />
                            <Route path=":memberID" element={<MembersRoute><MemberDetailRoute /></MembersRoute>} />
                            <Route path="campsites" element={<MembersRoute><MemberViewCampsitesRoute /></MembersRoute>} />
                            <Route path="dashboard" element={<MembersRoute><MemberDashboardRoute /></MembersRoute>} />
                            <Route path="message-board" element={<MembersRoute><MessageBoardRoute /></MembersRoute>} />
                            <Route path="message-board/section/:sectionID/thread/new" element={<MembersRoute><NewThreadRoute /></MembersRoute>} />
                            <Route path="message-board/section/:sectionID/thread/:threadID" element={<MembersRoute><MessageThreadRoute /></MembersRoute>} />
                            <Route path="message-board/section/:sectionID/thread/:threadID/reply/:replyID/edit" element={<MembersRoute><EditMessageRoute /></MembersRoute>} />
                        </Route>
                        <Route path="/accounts/:accountID" element={<MembersRoute><AccountDetail /></MembersRoute>} />
                        <Route path="/admin">
                            <Route index element={<AdministratorsRoute><AdminDashboardRoute /></AdministratorsRoute>} />
                            <Route path="accounts" element={<AdministratorsRoute><AccountsRoute /></AdministratorsRoute>} />
                            <Route path="pending-clubhouse-requests" element={<AdministratorsRoute><ClubhouseRequestsRoute /></AdministratorsRoute>} />
                            <Route path="members" element={<AdministratorsRoute><MembersManagementRoute /></AdministratorsRoute>} />
                            <Route path="members/new" element={<AdministratorsRoute><CreateMemberRoute /></AdministratorsRoute>} />
                            <Route path="members/:memberID" element={<AdministratorsRoute><MembersManagementDetailRoute /></AdministratorsRoute>} />
                            <Route path="campsites" element={<AdministratorsRoute><CampsitesRoute /></AdministratorsRoute>} />
                            <Route path="campsites/new" element={<AdministratorsRoute><CreateCampsiteRoute /></AdministratorsRoute>} />
                            <Route path="campsites/:campsiteID" element={<AdministratorsRoute><CampsiteDetailRoute /></AdministratorsRoute>} />
                            <Route path="message-board" element={<AdministratorsRoute><MessageBoardManagementRoute /></AdministratorsRoute>} />
                        </Route>
                        <Route path='/my-account' element={<SecureRoute><MyAccount /></SecureRoute>} />
                        <Route path='/messages' element={<SecureRoute><PrivateMessages /></SecureRoute>} />
                        <Route path="/messages/:threadID" element={<SecureRoute><PrivateMessageThread /></SecureRoute>} />
                        <Route path="/auth/sign-in" element={<NoAuthRoute><SignIn /></NoAuthRoute>} />
                        <Route path="/auth/login-callback" element={<NoAuthRoute><LoginCallback /></NoAuthRoute>} />
                        <Route path="/auth/forgot-password" element={<NoAuthRoute><ForgotPassword /></NoAuthRoute>} />
                        <Route path="/auth/create-account" element={<NoAuthRoute><CreateAccount /></NoAuthRoute>} />
                        <Route path="/auth/reset-password" element={<NoAuthRoute><ResetPassword /></NoAuthRoute>} />
                        <Route path="/auth/confirm" element={<NoAuthRoute><ConfirmAccount /></NoAuthRoute>} />
                        <Route path="/email-unsubscribe" element={<EmailUnsubscribe />} />
                    </Routes>
                </Wrapper>
            </Suspense>
        </Router>
    )
}

export default App